import { FORM_INITIAL_VALUES } from '@application/components/controllers/pages/LitterListPageController/constants';
import { BREEDER_LANGUAGE_CODES } from '@application/constants';
import type { PedigreeType } from '@application/types';
import {
  allLanguageSelectOptions,
  animalSizeSelectOptions,
  bornTypeSelectOptions,
  countrySelectOptions,
  useRouter,
} from '@application/utils';
import type { AnimalSizeEnum, CountryCode } from '@gql/graphql';
import {
  FiltersWithModalInputSelectField,
  FiltersWithModalInputWrapper,
  InputRangeField,
} from '@presentation/components';
import type { LitterListPageFormValues } from '@presentation/pages';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import { Form, useFormikContext } from 'formik';
import type { FC } from 'react';
import { Suspense } from 'react';
import { useMediaQuery } from 'react-responsive';
import type { FiltersProps } from './types';

export const Filters: FC<FiltersProps> = (props) => {
  const { options } = props;
  const { submitForm } = useFormikContext<LitterListPageFormValues>();
  const { language, countryCode } = useRouter();
  const { translate } = useTranslation();
  const { translate: translateRessource } = useTranslation({
    keyPrefix: 'common.inputs',
  });

  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.tablet}px)` });

  return (
    <Form>
      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<string>
          name="breedNames"
          placeholder={translateRessource('breeds.placeholder')}
          multiple
          dismissOnClick={false}
          options={options.breeds}
        />
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <Suspense fallback="...">
          <InputRangeField
            name="price"
            placeholder={translateRessource('price.placeholder')}
            limit={options.price.limit}
            labelFormatter={(value) => `${value}€`}
            onChange={() => submitForm()}
            clearButton={{
              showOnChange: true,
              initialValue: FORM_INITIAL_VALUES.price,
              onClear: () => submitForm(),
            }}
          />
        </Suspense>
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<AnimalSizeEnum>
          name="sizeIds"
          placeholder={translateRessource('size.placeholder')}
          multiple
          dismissOnClick={false}
          options={animalSizeSelectOptions(translate)}
        />
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<PedigreeType>
          name="pedigree"
          placeholder={translateRessource('pedigree.placeholder')}
          multiple
          options={[
            { value: 'FCI', label: translate('enum.pedigreeType.FCI') },
            { value: 'OTHER', label: translate('enum.pedigreeType.OTHER') },
          ]}
        />
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<boolean>
          name="born"
          placeholder={translateRessource('born.placeholder')}
          options={bornTypeSelectOptions(translate)}
        />
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<string>
          name="spokenLanguages"
          multiple
          dismissOnClick={false}
          placeholder={translateRessource('spokenLanguages.placeholder')}
          relativeDropdown={isMobile}
          options={allLanguageSelectOptions(language, BREEDER_LANGUAGE_CODES)}
        />
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<string>
          name="regions"
          multiple
          dismissOnClick={false}
          placeholder={translateRessource(`regions.placeholder.${countryCode}`)}
          relativeDropdown={isMobile}
          options={options.regions}
        />
      </FiltersWithModalInputWrapper>

      <FiltersWithModalInputWrapper>
        <FiltersWithModalInputSelectField<CountryCode>
          name="countries"
          multiple
          dismissOnClick={false}
          placeholder={translateRessource('country.placeholder')}
          relativeDropdown={isMobile}
          options={countrySelectOptions(translate)}
        />
      </FiltersWithModalInputWrapper>
    </Form>
  );
};
